/**
 * Echo Hunt -- the in-place repair rewrite prompt (P10, §9.3).
 *
 * VISIBILITY-MATRIX ROW (§16.0): "Repair rewrite".
 *  - knows real/fake?      NO. It is only ever invoked ON a real bullet (§9.3), but the prompt does not
 *                          say so and has no parameter for it -- the model is asked to make one sentence
 *                          fit better, which is true regardless of where the sentence came from.
 *  - sees world/character?  YES -- this is the second of the two steps allowed to (invariant #4). The
 *                          asymmetry is safe because the repair is followed by a retention check: the
 *                          event cannot be replaced with a more lore-appropriate one.
 *  - sees other bullets?    YES, the full story (a rewritten bullet has to still join its neighbours).
 *  - sees corpus?           NO.
 *
 * ## Why the repair menu exists at all (§9.3, §17)
 *
 * If only REAL bullets ever got a "fit the lore better" pass, then "suspiciously lore-fitting" would
 * become a cross-round tell pointing at real content -- the opposite of what the game wants to teach. The
 * loop therefore also resamples fakes and real-with-rewrite, so no single class owns the repair.
 *
 * ## Why the rewrite is bounded
 *
 * Two failure modes, both handled elsewhere:
 *  - **event erosion** -- the rewrite drifts into a different event. §9.3 gates it with the SAME
 *    retention check the reshape uses (`CheckRetention`), and after the per-slot budget the slot is
 *    RESAMPLED rather than rewritten again.
 *  - **the rewrite becoming the tell** -- §13.3's rewritten-vs-untouched hit-rate diagnostic reads this
 *    after the fact, which is why `rewriteCount` is logged per slot.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §9.3, §2 (#3, #4), §16.0, §16.2 P10.
 */
import type {JSONSchemaLike} from "../LLM.js";
import {HOUSE_STYLE_RULES} from "./IndexPrompt.js";

/** The repair rules. The FIRST rule is the load-bearing one: the repair exists to make the sentence sit
 * better in the story's world, never to change what happened (§9.3 "retention-gated"). */
export const REPAIR_RULES = [
	"Change ONE sentence -- the target. Every other sentence must be returned exactly as it was given.",
	"Keep the event in the target exactly as it is: the same action, the same objects, the same place, the same people, the same outcome.",
	"Make the target fit this installment and this world better: its wording, its place in the scene, and any detail that reads as belonging to another setting may change.",
	"Do not add a new event, a new character, a new place or a new explanation.",
	"Do not make the sentence more striking, more meaningful or more resolved. The goal is that it reads as an ordinary part of this story, not that it reads better.",
	"Do not smooth out anything strange that the sentence already contains. A strange thing that simply happens is exactly what is wanted.",
].map(a=>`- ${a}`).join("\n");

export function RepairRewrite_SystemPrompt(): string {
	return [
		"You revise a single sentence inside a short piece of prose so that it belongs where it is.",
		"You never change what happened in it.",
		"Reply with JSON only. No prose, no code fences, no explanation.",
	].join("\n");
}

export type RepairRewrite_UserPromptOptions = {
	/** the world the story is set in (§8.5) */
	worldName: string;
	worldBible: string;
	/** the character the story follows (§8.2) */
	characterName: string;
	/** the storyline coming into the installment, for context */
	summary?: string|n;
	/** the full installment in story order, `{id, text}` */
	bullets: {id: string, text: string}[];
	/** the id of the ONE bullet to rewrite */
	targetId: string;
	/** on a retry: the problems found in the previous reply */
	fixNotes?: string|n;
};

/** Builds the in-place repair prompt (§9.3).
 *
 * The full installment is included because the target has to still join its neighbours, and the world
 * bible because "fit better" is meaningless without it. `targetId` is the only thing that identifies
 * which sentence may change -- the reply must carry the OTHER ids' text back unchanged, and that is
 * checked in `Repair.ts` rather than trusted. */
export function RepairRewrite_UserPrompt(opts: RepairRewrite_UserPromptOptions): string {
	const {worldName, worldBible, characterName, summary, bullets, targetId, fixNotes} = opts;

	const parts = [
		"## The world",
		`Name: ${worldName}`,
		worldBible.trim(),
		"",
		"## The character",
		`Name: ${characterName}`,
		"",
	];

	if (summary != null && summary.trim().length > 0) {
		parts.push("## The story so far", summary.trim(), "");
	}

	parts.push(
		"## The installment",
		JSON.stringify(bullets.map(bullet=>({id: bullet.id, text: bullet.text})), null, 2),
		"",
		"## Your task",
		`Rewrite ONLY the sentence with id "${targetId}".`,
		REPAIR_RULES,
		"",
		"## House style",
		HOUSE_STYLE_RULES,
		"",
		"## Output",
		"Reply with ONE JSON object:",
		`{"text": "<the rewritten target sentence>"}`,
		"",
		"Hard requirements:",
		"- `text` is the rewritten TARGET sentence only. Do not return the other sentences, and do not return the whole installment.",
		"- One sentence. No headings, no bullet characters, no quotation marks.",
	);

	if (fixNotes != null && fixNotes.trim().length > 0) {
		parts.push(
			"",
			"## Fix these problems from your previous attempt",
			fixNotes.trim(),
		);
	}

	return parts.join("\n");
}

/** The JSON shape `CallLLM` validates each attempt against. */
export function RepairRewrite_JSONSchema(): JSONSchemaLike {
	return {
		type: "object",
		required: ["text"],
		properties: {
			text: {type: "string", minLength: 1},
		},
	};
}
